import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, Loader2, RefreshCw, SwitchCamera, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * MULTI-SHOT CAMERA — the camera stays LIVE. A shot never closes it: the frame is grabbed from the
 * running stream, queued, and uploaded in the background while the crew keeps shooting. Every shot
 * shows as a thumbnail in the session, can be dropped before Done, and a failed upload keeps its
 * image so it can be retried when the signal comes back (nothing captured is ever lost).
 *
 * `upload(file)` does the actual POST and resolves with the saved photo record. Used by the
 * installer completion card and by every material photo affordance.
 */
export default function CameraCapture({ open, onClose, upload, onDone, onDrop, title = "Photos" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [live, setLive] = useState(false);
  const [shots, setShots] = useState([]);       // { key, url, file, state: uploading|done|failed, record }
  const [starting, setStarting] = useState(true);
  const fileRef = useRef(null);

  // PHONE FRAME. iOS reports three different heights (innerHeight, 100vh, the visual viewport) and
  // the dialog is centred with a transform, so on a phone the frame drifted off the visible area:
  // the preview looked offset in portrait and the shutter fell below the fold in landscape. On any
  // short or narrow screen we stop centring and pin the frame to the VISUAL viewport instead, and
  // re-measure on rotation and when the browser chrome slides in or out.
  const [vp, setVp] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    const vv = window.visualViewport;
    const read = () => {
      const phone = window.innerWidth < 640 || window.innerHeight < 520;
      setVp(phone
        ? { w: Math.round(vv?.width || window.innerWidth), h: Math.round(vv?.height || window.innerHeight) }
        : null);
    };
    read();
    vv?.addEventListener("resize", read);
    vv?.addEventListener("scroll", read);
    window.addEventListener("resize", read);
    window.addEventListener("orientationchange", read);
    return () => {
      vv?.removeEventListener("resize", read);
      vv?.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
      window.removeEventListener("orientationchange", read);
    };
  }, [open]);
  const short = !!vp && vp.h < 520;

  const stop = useCallback(() => {
    (streamRef.current?.getTracks() || []).forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  }, []);

  const start = useCallback(async () => {
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1920 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setLive(true);
    } catch {
      setLive(false);   // no camera / denied / insecure context → the file picker below still works
    }
    setStarting(false);
  }, [facing]);

  useEffect(() => {
    if (!open) { stop(); return; }
    start();
    return stop;
  }, [open, facing, start, stop]);

  const push = async (file) => {
    const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const url = URL.createObjectURL(file);
    setShots((cur) => [...cur, { key, url, file, state: "uploading" }]);
    try {
      const record = await upload(file);
      setShots((cur) => cur.map((s) => (s.key === key ? { ...s, state: "done", record } : s)));
    } catch (e) {
      setShots((cur) => cur.map((s) => (s.key === key ? { ...s, state: "failed" } : s)));
      toast.error(e?.response?.data?.detail || "That photo has not uploaded yet — retry it");
    }
  };

  /** Grab the current frame WITHOUT touching the stream: the preview keeps running. */
  const shoot = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return;
    await push(new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }));
  };

  const retry = async (shot) => {
    setShots((cur) => cur.map((s) => (s.key === shot.key ? { ...s, state: "uploading" } : s)));
    try {
      const record = await upload(shot.file);
      setShots((cur) => cur.map((s) => (s.key === shot.key ? { ...s, state: "done", record } : s)));
    } catch {
      setShots((cur) => cur.map((s) => (s.key === shot.key ? { ...s, state: "failed" } : s)));
    }
  };

  /** Dropping a shot before Done really removes it — an already-uploaded frame is deleted, not
   *  just hidden, so the session ends with exactly what the crew kept. */
  const drop = async (shot) => {
    setShots((cur) => cur.filter((s) => s.key !== shot.key));
    if (shot.state === "done" && shot.record && onDrop) {
      try { await onDrop(shot.record); } catch { toast.error("That photo is still stored — delete it from the gallery"); }
    }
  };

  const done = (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    const pending = shots.filter((s) => s.state !== "done");
    if (pending.length) {
      toast.error(`${pending.length} photo(s) have not uploaded`, { description: "Retry them or drop them first" });
      return;
    }
    stop();
    onDone?.(shots.map((s) => s.record).filter(Boolean));
    onClose?.();
  };

  const uploaded = shots.filter((s) => s.state === "done").length;
  const closeOut = (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    stop();
    onDone?.(shots.filter((s) => s.state === "done").map((s) => s.record));
    onClose?.();
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) closeOut(); }}>
      {/* Phone-first frame: the live preview takes whatever height is left AFTER the controls, in
          portrait and in landscape, so the shutter is never pushed off the bottom of the screen. */}
      <DialogContent className="!flex flex-col max-w-none w-screen h-[100dvh] sm:max-w-2xl sm:w-full sm:h-[88vh]
                                p-0 gap-0 overflow-hidden bg-black text-white border-0 rounded-none sm:rounded-lg"
                     onInteractOutside={(e) => e.preventDefault()}
                     style={vp ? { top: 0, left: 0, transform: "none", width: `${vp.w}px`,
                                   height: `${vp.h}px`, maxWidth: "none", maxHeight: "none",
                                   borderRadius: 0 } : undefined}
                     data-testid="camera-capture">
        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 pr-10 bg-black/90
                        pt-[calc(0.5rem+env(safe-area-inset-top))]">
          <DialogTitle className="text-sm font-semibold truncate">{title}</DialogTitle>
          <span className="text-[11px] text-gray-300 shrink-0" data-testid="camera-session-count">
            {shots.length} photo{shots.length === 1 ? "" : "s"}
            {shots.length > uploaded ? ` · ${shots.length - uploaded} uploading` : ""}
          </span>
        </div>

        <div className="relative flex-1 min-h-0 bg-black">
          <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-contain"
                 data-testid="camera-preview" />
          {!live && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
              {starting ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                <>
                  <Camera className="w-8 h-8 text-gray-400" />
                  <div className="text-[12px] text-gray-300">
                    No camera available here — take or pick photos with the button below. They still stack up
                    in this session.
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {shots.length > 0 && (
          <div className={`shrink-0 flex gap-2 overflow-x-auto px-3 bg-black/80 ${short ? "py-1" : "py-2"}`}
               data-testid="camera-session-strip">
            {shots.map((s) => (
              <div key={s.key} className="relative shrink-0" data-testid={`camera-shot-${s.key}`}>
                <img src={s.url} alt="" className={`${short ? "w-10 h-10" : "w-14 h-14"} object-cover rounded border border-gray-700`} />
                {s.state === "uploading" && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </span>
                )}
                {s.state === "failed" && (
                  <button type="button" onClick={() => retry(s)} data-testid={`camera-retry-${s.key}`}
                          title="Upload failed — retry"
                          className="absolute inset-0 flex items-center justify-center bg-red-900/70 rounded">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                )}
                <button type="button" onClick={() => drop(s)} data-testid={`camera-drop-${s.key}`}
                        title="Drop this photo"
                        className="absolute -top-1.5 -right-1.5 bg-white text-gray-800 rounded-full p-0.5 border border-gray-300">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* One bar, three columns — the shutter sits dead centre whatever is either side of it. */}
        <div className={`shrink-0 grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-2 bg-black ${short ? "py-1" : "py-2"}`}
             style={{ paddingBottom: `calc(${short ? "0.25rem" : "0.5rem"} + env(safe-area-inset-bottom))` }}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
                 data-testid="camera-file-input"
                 onChange={async (e) => {
                   const files = [...(e.target.files || [])];
                   e.target.value = "";
                   for (const f of files) await push(f);
                 }} />
          <span className="flex items-center gap-1 min-w-0">
            <Button variant="ghost" className={`text-gray-300 px-2 ${short ? "h-9" : "h-11"}`} data-testid="camera-close"
                    onClick={(e) => closeOut(e)}>
              <Trash2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Close</span>
            </Button>
            <Button variant="ghost" className={`text-white px-2 text-[12px] ${short ? "h-9" : "h-11"}`} onClick={() => fileRef.current?.click()}
                    data-testid="camera-pick-file">Attach</Button>
          </span>
          {live ? (
            <button type="button" onClick={shoot} data-testid="camera-shutter"
                    className={`${short ? "w-12 h-12 border-[3px]" : "w-16 h-16 border-4"} rounded-full bg-white border-gray-400 active:scale-95 transition-transform`} />
          ) : (
            <Button className="h-12 px-5" onClick={() => fileRef.current?.click()} data-testid="camera-shutter-fallback">
              <Camera className="w-4 h-4 mr-2" /> Take a photo
            </Button>
          )}
          <span className="flex items-center justify-end gap-1 min-w-0">
            {live && (
              <Button variant="ghost" className={`text-white px-2 ${short ? "h-9" : "h-11"}`} data-testid="camera-flip"
                      onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}>
                <SwitchCamera className="w-5 h-5" />
              </Button>
            )}
            <Button className={`px-3 bg-blue-600 hover:bg-blue-700 ${short ? "h-9" : "h-11"}`} onClick={(e) => done(e)} data-testid="camera-done">
              <Check className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Done</span>{uploaded ? ` ${uploaded}` : ""}
            </Button>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
